// Canonical default age-transition values (single source of truth on the web side;
// mirrors the backend models.DefaultAgeTransition* constants).
export const AGE_TRANSITION_DEFAULTS = {
  months: 24,
  monthsSubsidised: 18,
  timing: 'FOLLOWING_MONTH' as const,
};

export interface InheritedAgeTransition {
  months: number;
  monthsSubsidised: number;
  timing: 'FOLLOWING_MONTH' | 'ON_BIRTHDAY';
}

// Structural accessor for the fields we read off a tariff version. Declared
// locally with both snake_case and camelCase variants, since callers hand this
// helper either the generated response or a camelCase transform of it.
// `TariffVersion` is structurally assignable to it, so no cast is needed.
export interface AgeTransitionVersionFields {
  groupType?: string;
  group_type?: string;
  status?: string;
  createdAt?: string;
  created_at?: string;
  ageTransitionMonths?: number;
  age_transition_months?: number;
  ageTransitionMonthsSubsidised?: number;
  age_transition_months_subsidised?: number;
  ageTransitionTiming?: string;
  age_transition_timing?: string;
}

/**
 * resolveInheritedAgeTransition returns the age-transition values inherited by a
 * NEW tariff version: those of the most-recent ACTIVE version of the same group
 * type, or the canonical defaults (24 / 18 / FOLLOWING_MONTH) when none exists.
 *
 * Used as the form prefill; the server-side service applies the same rule as the
 * authoritative fallback.
 */
export function resolveInheritedAgeTransition(
  versions: AgeTransitionVersionFields[] | undefined,
  groupType: string | undefined,
): InheritedAgeTransition {
  const fallback: InheritedAgeTransition = {
    months: AGE_TRANSITION_DEFAULTS.months,
    monthsSubsidised: AGE_TRANSITION_DEFAULTS.monthsSubsidised,
    timing: AGE_TRANSITION_DEFAULTS.timing,
  };
  if (!versions || versions.length === 0 || !groupType) {
    return fallback;
  }

  const candidates = versions
    .filter(
      (v) => (v.groupType || v.group_type) === groupType && v.status === 'ACTIVE',
    )
    .sort((a, b) => {
      const ad = a.createdAt || a.created_at || '';
      const bd = b.createdAt || b.created_at || '';
      return bd.localeCompare(ad); // most-recent (created_at DESC) first
    });

  const top = candidates[0];
  if (!top) {
    return fallback;
  }

  return extractAgeTransition(top);
}

/**
 * extractAgeTransition reads a single version's age-transition values (tolerating
 * both snake_case and camelCase), falling back to the canonical defaults for any
 * missing field. Used to prefill the form on copy/edit.
 */
export function extractAgeTransition(
  version: AgeTransitionVersionFields | undefined,
): InheritedAgeTransition {
  const fallback: InheritedAgeTransition = {
    months: AGE_TRANSITION_DEFAULTS.months,
    monthsSubsidised: AGE_TRANSITION_DEFAULTS.monthsSubsidised,
    timing: AGE_TRANSITION_DEFAULTS.timing,
  };
  if (!version) {
    return fallback;
  }
  const v = version;
  const months = v.ageTransitionMonths ?? v.age_transition_months ?? fallback.months;
  const monthsSubsidised =
    v.ageTransitionMonthsSubsidised ??
    v.age_transition_months_subsidised ??
    fallback.monthsSubsidised;
  const timingRaw = v.ageTransitionTiming ?? v.age_transition_timing ?? fallback.timing;
  const timing = timingRaw === 'ON_BIRTHDAY' ? 'ON_BIRTHDAY' : 'FOLLOWING_MONTH';

  return { months, monthsSubsidised, timing };
}
